/** StageState — ステージ進行状態管理(Phase A2 Day 6)。
 *  Reference: docs/STAGE_DESIGN.md §6.1 (JSON 仕様、Day 7 で StageLoader が本書を生成)
 *  - 'playing' / 'paused' / 'won' / 'lost' の 4 状態を持つ
 *  - target_coverage 達成判定、lives 管理
 *  - Day 7: StageLoader が JSON から StageState を生成
 *  - Lot 3: composition_mask 勝利条件サポート追加
 *
 * PM 統合TODO (Lot 3 受け渡し):
 * - GameplayScreen.ts は stageState.hasMaskVictory() が true の場合、
 *   territory.contains() を使って mask_cells の塗布率を計算し、
 *   stageState.setMaskProgress(progress) を呼ぶ必要がある
 * - レンダリング:mask_cells の輪郭を薄い色で描画(まだ塗ってないところを可視化)
 */

export type StageStatus = 'playing' | 'paused' | 'won' | 'lost';

export interface StageStateOptions {
  readonly stageId: string;
  readonly targetCoverage: number; // 0..1
  readonly initialLives: number;
  /** Lot 3: 'composition_mask' ステージでのみ設定。省略時は 'coverage_min' 相当の動作。 */
  readonly victoryConditionType?: 'coverage_min' | 'composition_mask' | string;
  /** Lot 3: composition_mask 時の mask 塗布割合閾値(0..1)。victoryConditionType が 'composition_mask' の時のみ有効。 */
  readonly maskTargetValue?: number;
}

export class StageState {
  public readonly stageId: string;
  public readonly targetCoverage: number;
  /** Lot 3: 勝利条件タイプ。未設定時は 'coverage_min' 相当で動作。 */
  private readonly victoryConditionType: string;
  /** Lot 3: composition_mask 時の閾値。 */
  private readonly maskTargetValue: number;

  private status: StageStatus = 'playing';
  private currentCoverage = 0;
  /** Lot 3: mask_cells のうち塗布済み割合(0..1)。composition_mask ステージのみ更新される。 */
  private currentMaskProgress = 0;
  private lives: number;
  private readonly initialLives: number;

  constructor(options: StageStateOptions) {
    this.stageId = options.stageId;
    this.targetCoverage = options.targetCoverage;
    this.lives = options.initialLives;
    this.initialLives = options.initialLives;
    this.victoryConditionType = options.victoryConditionType ?? 'coverage_min';
    this.maskTargetValue = options.maskTargetValue ?? options.targetCoverage;
  }

  getStatus(): StageStatus {
    return this.status;
  }

  getCurrentCoverage(): number {
    return this.currentCoverage;
  }

  getLives(): number {
    return this.lives;
  }

  setCoverage(coverage: number): void {
    this.currentCoverage = coverage;
    if (this.status === 'playing' && coverage >= this.targetCoverage) {
      this.status = 'won';
    }
  }

  decrementLives(): number {
    this.lives = Math.max(0, this.lives - 1);
    if (this.lives === 0 && this.status === 'playing') {
      this.status = 'lost';
    }
    return this.lives;
  }

  pause(): void {
    if (this.status === 'playing') this.status = 'paused';
  }

  resume(): void {
    if (this.status === 'paused') this.status = 'playing';
  }

  reset(): void {
    this.status = 'playing';
    this.currentCoverage = 0;
    this.currentMaskProgress = 0;
    this.lives = this.initialLives;
  }

  /** Continue 用:status と lives は再開、currentCoverage は維持する。
   *  EoR Continue ファネルから呼ばれ、プレイヤーは既に塗った領地はそのままに残機だけ復活する。
   */
  restoreForContinue(): void {
    this.status = 'playing';
    this.lives = this.initialLives;
    // currentCoverage は意図的に触らない(プレイヤーの努力を保護)
  }

  isPlaying(): boolean {
    return this.status === 'playing';
  }

  isWon(): boolean {
    return this.status === 'won';
  }

  isLost(): boolean {
    return this.status === 'lost';
  }

  // ── Lot 3: composition_mask 勝利条件 ──────────────────────────────────────

  /** primary.type === 'composition_mask' のステージかどうかを返す。
   *  GameplayScreen.ts が分岐ロジックを判断するために使う。
   */
  hasMaskVictory(): boolean {
    return this.victoryConditionType === 'composition_mask';
  }

  /** mask_cells のうち塗布済みの割合(0..1)を更新する。
   *  GameplayScreen.ts が毎フレーム territory.contains(cell) で塗布率を計算して渡す。
   *  maskTargetValue 以上になった時点で status を 'won' にする。
   */
  setMaskProgress(progress: number): void {
    this.currentMaskProgress = progress;
    if (this.status === 'playing' && progress >= this.maskTargetValue) {
      this.status = 'won';
    }
  }

  /** mask_cells の現在の塗布割合(0..1)を返す。UI 表示用。 */
  getMaskProgress(): number {
    return this.currentMaskProgress;
  }

  /** composition_mask 勝利条件で勝利済みかどうかを返す。
   *  isWon() と同義だが mask 専用の意図を明示する。
   */
  isMaskWon(): boolean {
    return this.hasMaskVictory() && this.status === 'won';
  }
}
